import React, { useState, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEBUG_ENDPOINT = `${API_URL}/debug/client-error`;

interface Message {
  text: string;
  timestamp: string;
  hasImage: boolean;
}

export function DebugChatBox() {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [sending, setSending] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [html2canvasAvailable, setHtml2canvasAvailable] = useState<boolean | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  // Pre-check if html2canvas is available (runs once when panel opens)
  React.useEffect(() => {
    if (isOpen && html2canvasAvailable === null) {
      import('html2canvas')
        .then(() => setHtml2canvasAvailable(true))
        .catch(() => setHtml2canvasAvailable(false));
    }
  }, [isOpen, html2canvasAvailable]);

  const captureScreen = async () => {
    if (capturing) return;
    setCapturing(true);
    try {
      // Dynamic import — html2canvas is optional
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        scale: 0.5, // half resolution for smaller file
        logging: false,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
      });
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
          setImage(file);
          setImagePreview(canvas.toDataURL('image/png', 0.6));
        }
      }, 'image/png', 0.6);
    } catch (err) {
      console.warn('Screen capture failed:', err);
      alert('Screenshot capture not available, please use Attach instead');
    } finally {
      setCapturing(false);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async () => {
    if (!text.trim() && !image) return;
    setSending(true);

    try {
      const payload: any = {
        type: image ? 'USER_FEEDBACK_IMAGE' : 'USER_FEEDBACK',
        message: text.trim() || '(screenshot only)',
        url: window.location.href,
      };

      if (image) {
        payload.imageName = image.name;
        payload.imageSize = `${Math.round(image.size / 1024)}KB`;
      }

      await fetch(DEBUG_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      setMessages(prev => [...prev, {
        text: text.trim() || '(screenshot)',
        timestamp: new Date().toLocaleTimeString(),
        hasImage: !!image
      }]);

      setText('');
      removeImage();
    } catch {
      // silently fail
    } finally {
      setSending(false);
    }
  };

  // Floating button styles
  const buttonStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '20px',
    left: '20px',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#DC2626',
    color: 'white',
    border: 'none',
    fontSize: '20px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s',
  };

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '80px',
    left: '20px',
    width: '340px',
    maxHeight: '480px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid #DC2626',
    overflow: 'hidden',
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={buttonStyle}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        title="Report a bug"
      >
        !
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(false)}
        style={{ ...buttonStyle, background: '#6B7280', fontSize: '16px' }}
      >
        ✕
      </button>

      <div style={panelStyle}>
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          background: '#DC2626',
          color: 'white',
          fontWeight: 600,
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>🐛 Report Issue</span>
          <span style={{ fontSize: '11px', opacity: 0.8 }}>Sent to dev</span>
        </div>

        {/* Message history */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 12px',
          maxHeight: '200px',
          background: '#F9FAFB'
        }}>
          {messages.length === 0 && (
            <p style={{ color: '#9CA3AF', fontSize: '12px', textAlign: 'center', margin: '16px 0' }}>
              Describe the issue you see on this page. Your message goes directly to the developer.
            </p>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{
              background: '#EFF6FF',
              borderRadius: '8px',
              padding: '8px 10px',
              marginBottom: '6px',
              fontSize: '12px'
            }}>
              <div style={{ color: '#1E40AF' }}>{m.text}</div>
              {m.hasImage && <div style={{ color: '#6B7280', fontSize: '11px' }}>📎 Screenshot attached</div>}
              <div style={{ color: '#9CA3AF', fontSize: '10px', marginTop: '4px' }}>
                ✓ Sent to dev • {m.timestamp}
              </div>
            </div>
          ))}
        </div>

        {/* Image preview */}
        {imagePreview && (
          <div style={{ padding: '8px 12px', background: '#F3F4F6', borderTop: '1px solid #E5E7EB' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={imagePreview}
                alt="Screenshot"
                style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
              />
              <button
                onClick={removeImage}
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#EF4444',
                  color: 'white',
                  border: 'none',
                  fontSize: '11px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Input area */}
        <div style={{ padding: '8px 12px', borderTop: '1px solid #E5E7EB' }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's wrong on this page?"
            style={{
              width: '100%',
              height: '60px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              padding: '8px 10px',
              fontSize: '13px',
              resize: 'none',
              outline: 'none',
              fontFamily: 'inherit'
            }}
          />
          <div style={{
            display: 'flex',
            gap: '6px',
            marginTop: '6px',
            alignItems: 'center'
          }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer',
                color: '#6B7280'
              }}
            >
              📎 Attach
            </button>
            {html2canvasAvailable !== false && (
              <button
                onClick={captureScreen}
                disabled={capturing}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  background: capturing ? '#F3F4F6' : 'white',
                  cursor: capturing ? 'not-allowed' : 'pointer',
                  color: capturing ? '#9CA3AF' : '#6B7280',
                  opacity: capturing ? 0.7 : 1,
                }}
              >
                {capturing ? '⏳ Capturing...' : '📸 Capture Page'}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              onClick={sendMessage}
              disabled={sending || (!text.trim() && !image)}
              style={{
                padding: '6px 16px',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                borderRadius: '6px',
                background: (sending || (!text.trim() && !image)) ? '#D1D5DB' : '#DC2626',
                color: 'white',
                cursor: (sending || (!text.trim() && !image)) ? 'not-allowed' : 'pointer'
              }}
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
